import { detectPlatform, type Platform } from "@z/ui";
import { useEffect, useRef } from "react";
import type { ActionContext, ActionRegistry } from "./registry";
import {
  bindingsFor,
  resolveKey,
  SEQUENCE_TIMEOUT_MS,
  shouldIgnoreTarget,
  type PendingSequence,
} from "./shortcuts";

export type GlobalShortcutsOptions = {
  /** Default true. */
  enabled?: boolean;
  /** Overrides platform detection (tests). */
  platform?: Platform;
  /** How long a sequence prefix like `g` waits for its next key. Default 1000 ms. */
  sequenceTimeoutMs?: number;
};

/**
 * One `keydown` listener on `window` that dispatches to the registry. Plain-key shortcuts are
 * ignored while an editable element has focus; `mod` shortcuts still fire there unless the
 * action sets `allowInEditor: false`. Sequence state lives in a ref, never in React state.
 *
 * `getContext` is read on every keypress; pass a stable function (the listener re-attaches
 * when its identity changes).
 */
export function useGlobalShortcuts(
  registry: ActionRegistry,
  getContext: () => ActionContext,
  options: GlobalShortcutsOptions = {},
): void {
  const { enabled = true, platform, sequenceTimeoutMs = SEQUENCE_TIMEOUT_MS } = options;
  const pendingRef = useRef<PendingSequence | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const resolvedPlatform = platform ?? detectPlatform();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.isComposing || event.repeat) {
        pendingRef.current = null;
        return;
      }
      // A modal owns the keyboard, including its buttons and non-editable surfaces.
      const path = event.composedPath();
      if (
        path.some(
          (target) =>
            target instanceof Element &&
            target.closest('[role="dialog"], [role="alertdialog"], [role="menu"]'),
        )
      ) {
        pendingRef.current = null;
        return;
      }
      // Shadow DOM retargets events to the host (e.g. the tree's rename input).
      // Inspect the original path so typing there cannot start navigation shortcuts.
      const inEditable =
        event.composedPath().some(shouldIgnoreTarget) || shouldIgnoreTarget(event.target);
      const ctx = getContext();
      const bindings = bindingsFor(registry.list(ctx), inEditable);
      const result = resolveKey(
        event,
        bindings,
        pendingRef.current,
        Date.now(),
        resolvedPlatform,
        sequenceTimeoutMs,
      );
      pendingRef.current = result.pending;
      if (result.actionId === undefined) {
        if (result.pending !== null) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      registry.run(result.actionId, ctx).catch((error: unknown) => {
        console.error(`Action "${result.actionId}" failed`, error);
      });
    };
    const clearPending = () => {
      pendingRef.current = null;
    };
    window.addEventListener("blur", clearPending);
    window.addEventListener("focusin", clearPending);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("blur", clearPending);
      window.removeEventListener("focusin", clearPending);
      pendingRef.current = null;
    };
  }, [registry, getContext, enabled, platform, sequenceTimeoutMs]);
}
